import { CloseOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import api from "API/api";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Formik, Form, Field, ErrorMessage } from "formik";
import React, { useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";

const AddDeviceModal = ({
  id,
  addDeviceOpen,
  editDeviceData,
  setAddDeviceOpen,
  getSitesList,
}) => {
  const { white, gradients } = colors;
  const [loading, setLoading] = useState(false);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "14px",
    outline: "none !important",
  };

  const validationSchema = Yup.object().shape({
    device_name: Yup.string()
      .required("Device Name is required")
      .min(3, "Device Name must be at least 3 characters"),
  });

  const initialValues = {
    device_name: editDeviceData?.device_name || "",
    type: editDeviceData?.type || "camera",
    streamType: editDeviceData?.streamType || "",
    device_video: null,
    device_url: editDeviceData?.device || "",
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append("site_id", Number(id));
    formData.append("device_name", values.device_name);
    formData.append("type", values.type);

    if (values.type === "camera") {
      if (values.streamType === "live" && values.device_url) {
        formData.append("device_url", values.device_url);
      } else if (values.streamType === "recorded" && values.device_video) {
        formData.append("device_video", values.device_video);
      }
    } else if (values.type === "live_stream") {
      if (values.device_url) {
        formData.append("device_url", values.device_url);
      }
    }
    try {
      setLoading(true);
      if (editDeviceData) {
        formData.append("device_id", editDeviceData?.device_id);
        const response = await api.put(`/cv/deviceapi`, formData);
        toast.success(response.data.message);
      } else {
        const response = await api.post("/cv/deviceapi", formData);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setTimeout(() => {
        setAddDeviceOpen(false);
        getSitesList();
      }, 2000);
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Modal open={addDeviceOpen} onClose={() => setAddDeviceOpen(false)}>
        <Box sx={style} className="add-device-modal">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting, dirty }) => (
              <Form>
                <Box
                  sx={{
                    mb: 3,
                    backgroundColor: gradients.info.main,
                    borderTopLeftRadius: "14px",
                    borderTopRightRadius: "14px",
                  }}
                >
                  <Box
                    sx={{
                      padding: "15px",
                      display: "flex",
                      justifyContent: "space-between",
                      color: white.main,
                    }}
                  >
                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                      {editDeviceData ? "EDIT DEVICE" : "ADD DEVICE"}
                    </Typography>
                    <CloseOutlined
                      onClick={() => setAddDeviceOpen(false)}
                      sx={{
                        cursor: "pointer",
                        fontSize: "30px",
                        mt: "5px",
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ padding: "15px" }}>
                  <MDBox mb={2}>
                    <Field
                      fullWidth
                      as={MDInput}
                      name="device_name"
                      placeholder="Enter Device Name"
                    />
                    <ErrorMessage name="device_name">
                      {(msg) => (
                        <MDTypography variant="caption" color="error">
                          {msg}
                        </MDTypography>
                      )}
                    </ErrorMessage>
                  </MDBox>

                  <MDBox mb={2}>
                    <FormControl variant="outlined" fullWidth controlId="type">
                      <InputLabel id="type">Device Type</InputLabel>
                      <Field
                        name="type"
                        as={Select}
                        label="Device Type"
                        labelId="type"
                        fullWidth
                        style={{
                          height: "44px",
                          fontSize: "16px",
                          borderRadius: "5px",
                        }}
                      >
                        <MenuItem value="camera">Camera</MenuItem>
                        <MenuItem value="live_stream">Stream Url</MenuItem>
                        {/* <MenuItem value="scada">Scada</MenuItem> */}
                      </Field>
                    </FormControl>
                  </MDBox>

                  <MDBox>
                    {values.type === "camera" && (
                      <FormControl fullWidth>
                        <InputLabel>Stream Type</InputLabel>
                        <Field
                          name="streamType"
                          as={Select}
                          fullWidth
                          label="Stream Type"
                          style={{
                            height: "44px",
                            fontSize: "16px",
                            borderRadius: "5px",
                          }}
                        >
                          <MenuItem value="recorded">Recorded</MenuItem>
                          <MenuItem value="live">Live</MenuItem>
                        </Field>
                      </FormControl>
                    )}
                  </MDBox>

                  <MDBox mb={2}>
                    {values.type === "camera" &&
                      values.streamType === "recorded" && (
                        <>
                          <input
                            name="device_video"
                            type="file"
                            accept=".mp4"
                            onChange={(e) => {
                              setFieldValue("device_video", e.target.files[0]);
                            }}
                            className="file-upload"
                            style={{ width: "100%" }}
                          />
                        </>
                      )}
                  </MDBox>

                  {values.type === "camera" && values.streamType === "live" && (
                    <MDBox mb={2}>
                      <Field
                        name="device_url"
                        type="text"
                        placeholder="Enter device URL"
                        as={MDInput}
                        fullWidth
                      />
                      <ErrorMessage
                        name="device_url"
                        component="div"
                        className="text-danger"
                      />
                    </MDBox>
                  )}

                  {values.type === "live_stream" && (
                    <MDBox mb={2}>
                      <Field
                        name="device_url"
                        type="text"
                        as={MDInput}
                        fullWidth
                        placeholder="Enter Device IP Addr"
                      />
                      <ErrorMessage
                        name="device_url"
                        component="div"
                        className="text-danger"
                      />
                    </MDBox>
                  )}

                  <MDBox mt={4} mb={1}>
                    <Button
                      type="submit"
                      sx={{
                        height: "38px",
                        width: "100%",
                        fontSize: "14px",
                        fontWeight: "500",
                        background: !dirty
                          ? "#e0e0e0"
                          : `${gradients.info.main}`,
                        color: white.main,
                        border: "none",
                        marginRight: "0",
                        marginBottom: "0",
                        "&:hover": {
                          background: `${gradients.info.state} !important`,
                          color: white.main,
                        },
                      }}
                      disabled={loading || !dirty}
                    >
                      {loading ? (
                        <CircularProgress
                          size={25}
                          sx={{
                            color: "white !important",
                          }}
                        />
                      ) : isSubmitting ? (
                        editDeviceData ? (
                          "Updating..."
                        ) : (
                          "Submitting..."
                        )
                      ) : editDeviceData ? (
                        "Update"
                      ) : (
                        "Add"
                      )}
                    </Button>
                  </MDBox>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </React.Fragment>
  );
};

export default AddDeviceModal;
